import Vue from 'vue'
import Vuex from 'vuex'
import uniqid from 'uniqid'

Vue.use(Vuex)

const SERVER_URL = process.env.NODE_ENV === 'production' ? 'https://newlayers.verdedata.ro' : 'http://localhost:3535'

export default new Vuex.Store({
  state: {
    loading: false,
    projects: [],
    selectedProject: null,
    selectedLayer: null,
    snack: {
      message: null,
      type: 'error',
      open: false
    }
  },
  getters: {
    snack: state => state.snack,
    loading: state => state.loading,
    projects: state => state.projects,
    selectedProject: state => state.selectedProject,
    layers: state => state.selectedProject ? state.projects.find(project => project.id === state.selectedProject).layers : null,
    selectedLayer: state => state.selectedLayer,
    currentLayer: state => state.selectedProject && state.selectedLayer ? state.projects.find(project => project.id === state.selectedProject).layers.find(layer => layer.id === state.selectedLayer) : null,
    currentProject: state => state.selectedProject ? state.projects.find(project => project.id === state.selectedProject) : null,
    chosenLayers: state => state.selectedProject ? state.projects.find(project => project.id === state.selectedProject).layers.find(layer => layer.selected) : null
  },
  mutations: {
    setLoading (state, payload) {
      state.loading = payload
    },
    setSelectedProject (state, payload) {
      state.selectedProject = payload
    },
    setSelectedLayer (state, payload) {
      state.selectedLayer = payload
    },
    setSnack (state, payload) {
      state.snack = Object.assign({}, payload)
    },
    newProject (state, payload) {
      state.projects.push(payload)
    },
    newLayer (state, payload) {
      const index = state.projects.findIndex(project => project.id === state.selectedProject)
      if (index !== -1) {
        state.projects[index].layers.push(payload)
      }
    },
    deleteProject (state) {
      const index = state.projects.findIndex(project => project.id === state.selectedProject)
      if (index !== -1) {
        state.projects.splice(index, 1)
        state.selectedProject = null
        state.selectedLayer = null
      }
    },
    deleteLayer (state) {
      const index = state.projects.findIndex(project => project.id === state.selectedProject)
      if (index !== -1) {
        const layerIndex = state.projects[index].layers.findIndex(layer => layer.id === state.selectedLayer)
        if (layerIndex !== -1) {
          state.projects[index].layers.splice(layerIndex, 1)
          state.projects[index].markers = state.projects[index].markers.map(marker => {
            if (marker.layer === state.selectedLayer) {
              marker.layer = null
              marker.zona = null
            }
            return marker
          })
          state.selectedLayer = null
        }
      }
    },
    setLayerCoord (state, payload) {
      const layer = state.projects.find(project => project.id === state.selectedProject).layers.find(layer => layer.id === state.selectedLayer)
      layer.coords.push(payload)
    },
    setLayerPath (state, payload) {
      const project = state.projects.find(project => project.id === state.selectedProject)
      const layer = project.layers.find(layer => layer.id === payload.id)
      Vue.set(layer, 'coords', payload.coords)
    },
    changeCurrentProjectTitle (state, payload) {
      if (state.selectedProject) {
        const project = state.projects.find(project => project.id === state.selectedProject)
        Vue.set(project, 'title', payload)
      }
    },
    changeCurrentLayerTitle (state, payload) {
      if (state.selectedLayer && state.selectedProject) {
        const layer = state.projects.find(project => project.id === state.selectedProject).layers.find(layer => layer.id === state.selectedLayer)
        Vue.set(layer, 'title', payload)
      }
    },
    changeCurrentLayerColor (state, payload) {
      if (state.selectedLayer && state.selectedProject) {
        const layer = state.projects.find(project => project.id === state.selectedProject).layers.find(layer => layer.id === state.selectedLayer)
        Vue.set(layer, 'color', payload)
      }
    },
    setChosenLayers (state, payload) {
      if (state.selectedProject) {
        const project = state.projects.find(project => project.id === state.selectedProject)
        if (project) {
          project.layers.forEach((layer, index) => {
            if (payload.includes(layer.id)) {
              Vue.set(project.layers[index], 'selected', true)
            } else {
              Vue.set(project.layers[index], 'selected', false)
            }
          })
        }
      }
    },
    setShowLayerMarkers (state, payload) {
      if (state.selectedProject) {
        const project = state.projects.find(project => project.id === state.selectedProject)
        project.layers = project.layers.map(layer => {
          if (payload.includes(layer.id)) {
            layer.showMarkers = true
          } else {
            layer.showMarkers = false
          }
          return layer
        })
      }
    },
    setClusteringLevel (state, payload) {
      if (state.selectedProject) {
        const project = state.projects.find(project => project.id === state.selectedProject)
        if (project) {
          Vue.set(project, 'clusteringLevel', payload)
        }
      }
    },
    computeLayerMarkers (state, payload) {
      const project = state.projects.find(project => project.id === state.selectedProject)
      project.layers = project.layers.map(layer => {
        layer.markers = []
        return layer
      })

      if (project.markers.length) {
        const google = payload.google
        const markers = project.markers.map(marker => {
          marker.layer = null
          marker.zona = null
          project.layers.forEach(layer => {
            const poly = new google.maps.Polygon({paths: layer.coords})
            const point = new google.maps.LatLng({lat: +marker.lat, lng: +marker.lng})
            const contains = google.maps.geometry.poly.containsLocation(point, poly)
            if (contains) {
              marker.layer = layer.id
              layer.markers.push(marker.id)
              marker.zona = layer.title
            }
          })

          return marker
        })
        Vue.set(project, 'markers', markers)
      }
    },
    setShowProjectMarkers (state, payload) {
      if (state.selectedProject) {
        const project = state.projects.find(project => project.id === state.selectedProject)
        Vue.set(project, 'showMarkers', payload)
      }
    }
  },
  actions: {
    setSelectedProject ({commit}, payload) {
      commit('setSelectedProject', payload)
      commit('setSelectedLayer', null)
    },
    setSelectedLayer ({commit}, payload) {
      commit('setSelectedLayer', payload)
    },
    addNewProject ({commit, state}, payload) {
      commit('setLoading', true)
      const headersIndex = payload.data.markersData.findIndex(row => row.some(c => c === 'Nr. crt.'))
      const headersRow = payload.data.markersData.splice(headersIndex, 1)
      const headers = headersRow[0].filter(h => h)

      const markers = payload.data.markersData.map(row => {
        if (row[1] !== null && row[1] !== 'Nr. crt.') {
          const m = {}

          headers.forEach((h, i) => {
            m[h] = row[i]
          })

          m.layer = null
          return m
        }
        return null
      }).filter(item => item)

      // debugger

      const layers = payload.data.layersData.map(row => {
        if (row[0] !== null && row[0] !== 'id') {
          const layer = {}
          layer.id = row[0]
          layer.title = row[1]
          layer.color = row[2]
          layer.markers = []
          layer.coords = []

          row.forEach((cell, index) => {
            if (index > 2) {
              if (String(cell).includes(';;;;;')) { // markers
                const parts = cell.split(';;;;;')
                parts.forEach(part => {
                  if (!isNaN(part) && part) {
                    const marker = markers.find(marker => marker.id === +part)
                    if (marker) {
                      layer.markers.push(marker.id)
                      marker.layer = layer.id
                    }
                  }
                })
              } else { // path
                layer.coords = payload.google.maps.geometry.encoding.decodePath(cell)
              }
            }
          })

          return layer
        }
        return null
      }).filter(layer => layer)

      const id = uniqid()
      commit('newProject', {id, title: payload.data.title, layers, markers, clusteringLevel: payload.data.clusteringLevel, showMarkers: payload.data.showMarkers, headers})
      commit('setSelectedProject', id)
      commit('setLoading', false)
      commit('setSnack', {message: `${markers.length} markere citite.`, type: 'success', open: true})
    },
    addNewLayer ({commit, state}, payload) {
      if (state.selectedProject) {
        const id = uniqid()
        commit('newLayer', {...payload, markers: [], id, coords: []})
        commit('setSelectedLayer', id)

        fetch(SERVER_URL + '/icons', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: JSON.stringify({ color: payload.color })
        })
      }
    },
    changeCurrentLayerColor ({ commit }, payload) {
      fetch(SERVER_URL + '/icons', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify({ color: payload })
      })
      commit('changeCurrentLayerColor', payload)
    },
    deleteProject ({commit, state}) {
      commit('deleteProject')
    },
    deleteLayer ({commit, state}) {
      commit('deleteLayer')
    }
  }
})
